import React, { useEffect, useMemo, useState } from "react";
import { getAssociations, startScan, exportAssociationsUrl, deleteAssociation } from "../lib/api";
import { useCountry } from "../lib/countryContext";
import { SectionTitle, PrimaryButton, SecondaryButton, EmptyState } from "../components/UIKit";
import { Lightning, DownloadSimple, MagnifyingGlass, TrashSimple, Handshake } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = ["all", "trade_association", "distributor", "financing", "insurance", "marketing_agency", "certification", "utility", "other"];

export default function AssociationsPage() {
  const { selected, countries } = useCountry();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const country = countries.find((c) => c.code === selected);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getAssociations({ country_code: selected, q: q || undefined, type });
      setItems(data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [selected, type]);

  const runScan = async () => {
    setStarting(true);
    try {
      const r = await startScan("associations", selected);
      if (r.already_running) toast.info(`Scan already running for ${country?.name}.`);
      else toast.success(`Scan started for ${country?.flag} ${country?.name}.`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start scan");
    }
    setStarting(false);
  };

  const remove = async (id) => {
    await deleteAssociation(id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl">
      <SectionTitle overline={`${country?.flag || ""} ${country?.name || "COUNTRY"}`} title="Potential Buyers">
        <PrimaryButton testId="associations-scan-btn" onClick={runScan} disabled={starting}>
          <Lightning size={14} weight="bold" className="inline mr-1.5" />
          {starting ? "STARTING…" : "SCAN MORE"}
        </PrimaryButton>
        <SecondaryButton testId="associations-export-btn" as="a" href={exportAssociationsUrl(selected)}>
          <DownloadSimple size={14} weight="bold" /> EXPORT CSV
        </SecondaryButton>
      </SectionTitle>

      <div className="border border-black bg-white p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] border border-black/25 px-2 py-1">
          <MagnifyingGlass size={14} weight="bold" />
          <input
            data-testid="associations-search"
            placeholder="Search name, city, why-buy…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchData()}
            className="w-full outline-none text-sm"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {TYPES.map((s) => (
            <button
              key={s}
              data-testid={`filter-type-${s}`}
              onClick={() => setType(s)}
              className={`px-2.5 py-1.5 text-[10px] tracking-[0.15em] uppercase border transition-colors duration-150 ${
                type === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/25 hover:bg-black/5"
              }`}
              style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
        <div className="text-xs text-[#525252] tabular-nums ml-auto" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          {filtered.length} results
        </div>
      </div>

      {loading && !items.length ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Handshake size={22} weight="bold" />}
          title={`No buyers yet in ${country?.name || "this country"}`}
          hint="These are the organizations that would pay for your builder contact list. Scan finds trade associations, distributors, financing companies, insurers and more."
          action={
            <PrimaryButton testId="empty-scan-associations" onClick={runScan} disabled={starting}>
              <Lightning size={14} weight="bold" className="inline mr-1.5" /> RUN BUYER SCAN
            </PrimaryButton>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="associations-grid">
          {filtered.map((a) => (
            <div
              key={a.id}
              data-testid={`association-card-${a.id}`}
              className="border border-black/15 bg-white p-4 hover:border-black transition-colors duration-150"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#0047FF] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    {(a.type || "other").replace("_", " ")}
                  </div>
                  <div className="text-lg tracking-tight leading-none" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
                    {a.name}
                  </div>
                </div>
                <button
                  data-testid={`delete-assoc-${a.id}`}
                  onClick={() => remove(a.id)}
                  className="text-[#525252] hover:text-red-600 shrink-0"
                >
                  <TrashSimple size={14} weight="bold" />
                </button>
              </div>

              <div className="mt-3 text-sm text-[#0A0A0A]/80 leading-snug" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                {a.why_buy || "—"}
              </div>

              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                {a.city && <span className="text-[#525252]">📍 {a.city}</span>}
                {a.website && (
                  <a className="text-[#0047FF] underline" href={a.website} target="_blank" rel="noreferrer">
                    {a.website.replace(/^https?:\/\//, "").replace(/^www\./, "").slice(0, 30)}
                  </a>
                )}
                {a.email && <span>✉ {a.email}</span>}
                {a.phone && <span>☎ {a.phone}</span>}
              </div>

              {a.value_estimate && (
                <div className="mt-3 inline-block bg-[#D4FF00] text-[#0A0A0A] px-2 py-1 text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  Value: {a.value_estimate}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
